import { useAppDispatch, useAppSelector } from '../redux';
import { useCallback, useEffect, useState } from 'react';
import { ApiRequestStatus, GlobalActions } from '../../model';
import { useIntl } from 'react-intl';
import { appRoutes } from '../../routing';
import commonErrorMessages from '../../messages/common-messages/common-error.messages';
import { verifySetPasswordCredentials } from './set-password-utils';
import { postUpdateProspectOnboarding } from "../../store";

export interface SetPasswordLogicCallbacks {
  newPassword: string;
  confirmNewPassword: string;
  status: ApiRequestStatus;
  setNewPassword: (string) => void;
  setConfirmNewPassword: (string) => void;
  handleSubmit: () => void;
}

export const useSetPasswordPageLogic = (
  actions: GlobalActions
): SetPasswordLogicCallbacks => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const { navigate, showToast, closeToast } = actions;

  const [newPassword, setNewPassword] = useState<string>();
  const [confirmNewPassword, setConfirmNewPassword] = useState<string>();

  const [feError, setFeError] = useState<any | undefined>(undefined);

  const referralCode = useAppSelector(
    (state) => state.features.finalizeOnboarding.referralCode
  );

  const { status, error, code } = useAppSelector(
    (state) => state.features.finalizeOnboarding.updateProspectRequest
  );

  const handleSubmit = useCallback(() => {
    const e = verifySetPasswordCredentials({
      newPassword,
      newPasswordConfirm: confirmNewPassword,
    });
    if (e) setFeError(e);
    else {
      setFeError(undefined);
      if (referralCode)
        dispatch(postUpdateProspectOnboarding({referralCode,
        secret: newPassword}))
    }
  }, [dispatch, referralCode, newPassword, confirmNewPassword]);

  useEffect(() => {
    if (status === 'succeeded' && !error) {
      if (code !== '0') {
        showToast({
          type: 'error',
          title: formatMessage(commonErrorMessages.errorTitleDefault),
          description: formatMessage(
            commonErrorMessages.errorDescriptionDefault
          ),
          close: closeToast,
        });
      } else {
        navigate(appRoutes.ThankYouPage, { replace: true });
      }
    }
  }, [status, navigate, error]);

  useEffect(() => {
    if (error) {
      showToast({
        type: 'error',
        title: formatMessage(commonErrorMessages.errorTitleDefault),
        description: formatMessage(commonErrorMessages.errorDescriptionDefault),
        close: closeToast,
      });
    }
  }, [error, dispatch, formatMessage, showToast, closeToast]);

  useEffect(() => {
    if (feError) {
      showToast({
        type: 'error',
        title: formatMessage(feError.title),
        description: feError.description && formatMessage(feError.description),
        close: closeToast,
      });
      setFeError(undefined);
    }
  }, [feError, formatMessage, showToast, closeToast]);

  return {
    newPassword,
    confirmNewPassword,
    setConfirmNewPassword,
    setNewPassword,
    handleSubmit,
    status,
  };
};
